<template>
  <TButton
    variant="highlight"
    :disabled="isDisabled"
    :size="size"
    :block="!inlineButton"
   
    @click="goToRequestToBook"
  >
    <TIcon
      v-if="isInstabook"
      :icon="['fas', 'bolt']"
      class="mr-2"
    />

    {{ computedButtonText }}
  </TButton>
</template>

<script setup lang="ts">
import { ROUTE_BASE_NAME } from '~/constants'

import type { ButtonSize } from '~/components/_tailwind/button/types'

const props = withDefaults(defineProps<{
  size?: ButtonSize
  inlineButton?: boolean
  dataTestId?: string
  pageSource?: string
  enquiryId?: number
  isDestinationRequired?: boolean
}>(), {
  size: 'md',
  dataTestId: 'request-to-book',
})

const emit = defineEmits<{
  'open-breakdown': [field?: 'calendar' | 'destination']
  'close-breakdown': []
}>()

const { t } = useI18n()
const route = useRoute()
const localePath = useLocalePath()

const { isWebView } = usePlatform()
const { isLoggedIn } = useAuthentication()
const { userId, userProfileState } = useUser()
const { bookingEstimateData, bookingEstimateError, bookingEstimateParams, hasIncompleteDateRange, hasIncompleteDestination } = useBookingEstimate()
const { rvAliasName, rvId, rvOwnerId, rvIsStatic, rvIsPublished } = useRvDetails()
const { routeBaseName } = useBaseName()
const { showSignup } = useSignup()
const { getExperimentVariant } = useExperiment()

const profileCompletionPending = useProfileCompletionPending()
const authenticationModalShown = useAuthenticationModal()
const authenticationIntent = useAuthenticationIntent()

const isSimplifiedRtbFlow = computed(() => getExperimentVariant('rvz28661SimplifiedRtbFlow') === 'variant')

const isUserAlsoOwner = computed(() => rvOwnerId.value === userId.value)

const needsDestination = computed(() => isSimplifiedRtbFlow.value && props.isDestinationRequired && !rvIsStatic.value && hasIncompleteDestination.value)

const isGuiding = computed(() => hasIncompleteDateRange.value || needsDestination.value)

const isInstabook = computed(() => !isGuiding.value && Boolean(bookingEstimateData.value?.InstabookAvailable))

const isDisabled = computed(() => isUserAlsoOwner.value || !rvIsPublished.value || (!isGuiding.value && Boolean(bookingEstimateError.value)))

async function goToRequestToBook() {
  if (hasIncompleteDateRange.value) {
    emit('open-breakdown', 'calendar')
    return
  }

  if (needsDestination.value) {
    emit('open-breakdown', 'destination')
    return
  }

  if (!bookingEstimateData.value) {
    emit('open-breakdown')
    return
  }

  emit('close-breakdown')

  // Redirect to request to book
  const augmentedQuery = {
    from: props.pageSource ?? routeBaseName.value,
    enquiryid: props.enquiryId,
    StartDate: bookingEstimateParams.value.dateStart,
    EndDate: bookingEstimateParams.value.dateEnd,
    Adults: bookingEstimateParams.value.adults,
    Children: bookingEstimateParams.value.children,
    RvUsage: bookingEstimateParams.value.rvUsage ?? undefined,
    PlaceId: bookingEstimateParams.value.destination?.placeId,
    ...route.query,
  }

  const requestToBookUrl = localePath({
    name: isSimplifiedRtbFlow.value
      ? ROUTE_BASE_NAME.request_to_book
      : ROUTE_BASE_NAME.rv_rental_request_to_book_alias,
    params: { alias: rvAliasName.value },
    query: augmentedQuery,
  })

  if (isWebView.value && !isLoggedIn.value) {
    messageAppToSignup({
      successUrl: `${window.location.origin}${requestToBookUrl}`,
      failureUrl: window.location.href,
      cookies: {
        [rvId.value]: JSON.stringify({
          DateStart: bookingEstimateParams.value.dateStart,
          DateEnd: bookingEstimateParams.value.dateEnd,
          Adults: bookingEstimateParams.value.adults,
          Children: bookingEstimateParams.value.children,
          Pets: bookingEstimateParams.value.pets,
          Delivery: bookingEstimateParams.value.rvUsage === 'Delivery',
        }),
      },
    })
    return
  }

  if (isSimplifiedRtbFlow.value && userProfileState.value !== 'Complete') {
    usePendingRequestToBookUrlCookie().value = requestToBookUrl

    if (!isLoggedIn.value) {
      showSignup({ intent: 'BookingIntent' })
      return
    }

    authenticationIntent.value = 'BookingIntent'
    profileCompletionPending.value = true
    authenticationModalShown.value = true

    return
  }

  await navigateTo(requestToBookUrl)
}

const computedButtonText = computed(() => {
  if (hasIncompleteDateRange.value) {
    return t('checkAvailability')
  }

  if (needsDestination.value) {
    return t('selectHowToGetTheRv')
  }

  if (isInstabook.value) {
    return t('bookNow')
  }

  if (props.isDestinationRequired) {
    return t('actions.continue')
  }

  return t('requestToBook')
})
</script>

<i18n lang="json">
{
  "en": {
    "checkAvailability": "Check availability",
    "bookNow": "Book now",
    "requestToBook": "Request to book",
    "selectHowToGetTheRv": "Choose how to get the RV"
  },
  "fr": {
    "checkAvailability": "Vérifier la disponibilité",
    "bookNow": "Réserver maintenant",
    "requestToBook": "Demande de réservation",
    "selectHowToGetTheRv": "Choisir comment obtenir le VR"
  }
}
</i18n>
